// Owner rule (2026-08-29): an error message may reach the user ONLY when it
// tells them to take an action that is genuinely theirs to take ("code
// expired", "league is full", "you already manage a team here"). An app
// error - timeout, network, 5xx, our own internal validation, anything raw
// from axios - is OUR error: it goes to Sentry and the user gets the neutral
// fallback copy for that surface.
//
// HTTP status is deliberately NOT used as a proxy for actionability. A 4xx can
// still be caused by our generated payload or contain raw backend diagnostics.
import * as Sentry from '@sentry/react';
import { actionableMessage, ApiError, diagnosticCause } from '@/utils/appError';
import { ErrorReportDedupe } from '@/utils/errorReportDedupe';

// Protect both render-time call sites (same object) and polled requests (new
// error object on every attempt). A client reports the same diagnostic in the
// same section at most once per five-minute outage window.
const errorReportDedupe = new ErrorReportDedupe();

export function isUserActionableError(err: unknown): boolean {
  return actionableMessage(err) !== null;
}

/**
 * Returns the message that may be shown to the user for `err`.
 * - explicitly user-actionable error: its safe, intentional message.
 * - anything else: `fallback`, and the real error is reported to Sentry.
 */
export function userFacingError<T extends string | null>(
  err: unknown,
  fallback: T,
  context?: { section?: string; [key: string]: unknown },
): string | T {
  const publicMessage = actionableMessage(err);
  if (publicMessage) return publicMessage;
  if (errorReportDedupe.shouldReport(err, context?.section)) {
    try {
      Sentry.captureException(diagnosticCause(err), {
        tags: context?.section ? { section: context.section } : undefined,
        extra: {
          ...context,
          ...(err instanceof ApiError
            ? { apiStatus: err.status, apiCode: err.code, apiDiagnostic: err.message }
            : {}),
        },
      });
    } catch { /* never let telemetry break the UI */ }
  }
  return fallback;
}
